/**
 * 用户级功能模块开关。
 *
 * 每个 user 在 preferences_json.modules 里存 { module_id: boolean }。
 * 缺失或 undefined → 用 catalog 的 defaultEnabled。
 * forced=true 永远启用，UI 不显示开关。
 */

export interface ModuleSpec {
  id: string;
  label: string;
  description?: string;
  /** 该模块管辖的路由前缀。"/" 仅匹配根路径。 */
  paths: string[];
  /** 强制开启（如基础数据、设置）；UI 上不可关。 */
  forced?: boolean;
  /** 用户首次登录时该模块是否默认启用。 */
  defaultEnabled: boolean;
}

export const MODULES: ModuleSpec[] = [
  {
    id: "core",
    label: "📊 基础数据",
    description: "首页 / 股票 / 自选 / 选股 — 核心数据视图，不能关。",
    paths: ["/", "/stocks", "/block-trades", "/watchlist", "/scanner", "/more-tools", "/library", "/ml/publishing", "/lab/adaptive"],
    forced: true,
    defaultEnabled: true,
  },
  {
    id: "briefing",
    label: "📰 早报 + 市场体检 + 个股摘要 + 问答 + Copilot",
    description: "每日 LLM 早报、市场体检、个股 AI 摘要、自然语言问答（RAG）、AI Copilot 多步执行。",
    paths: [
      "/briefing", "/market-brief", "/rps", "/rotation", "/etf", "/indicators",
      "/topgun", "/theme-radar", "/ask", "/copilot", "/screen-runs",
    ],
    defaultEnabled: true,
  },
  {
    id: "news",
    label: "📡 新闻 + 情感",
    description: "新闻列表、情感打标、向量检索。",
    paths: ["/news", "/watch-news"],
    defaultEnabled: true,
  },
  {
    id: "patterns",
    label: "📐 形态匹配",
    description: "K 线形态模板 + 多尺度 DTW 匹配 + 命中告警。",
    paths: ["/patterns", "/analog"],
    defaultEnabled: true,
  },
  {
    id: "ml",
    label: "🤖 AI 预测",
    description: "创建预测实验、比较效果、跟踪预测结果并自动优化。",
    paths: ["/ml", "/auto-tune"],
    defaultEnabled: true,
  },
  {
    id: "alerts",
    label: "🔔 提醒与通知",
    description: "设置价格、风险和盘中变化提醒，并通过浏览器接收通知。",
    paths: ["/alerts", "/intraday-tracking", "/notifications"],
    defaultEnabled: true,
  },
  {
    id: "positions",
    label: "💼 持仓与交易管理",
    description: "查看持仓收益、记录交易、分析风险，并管理日内降成本策略；历史验证统一进入研究页。",
    paths: ["/positions", "/swing", "/grid", "/t-recap", "/risk", "/journal"],
    defaultEnabled: true,
  },
  {
    id: "trading",
    label: "🖥️ 交易执行",
    description: "连接券商并管理订单与成交；实盘 Agent 订单仍需逐单审批。",
    paths: ["/broker-trading"],
    defaultEnabled: true,
  },
  {
    id: "lab",
    label: "🔬 研究与策略",
    description: "研究、对照实验、因子和股票池；交易策略在独立交易区管理。",
    paths: ["/lab", "/grid/research", "/quant-agents"],
    defaultEnabled: true,
  },
  {
    id: "settings",
    label: "⚙️ 设置",
    description: "通知通道 / 账户 / 模块开关。",
    paths: ["/settings", "/settings/accounts", "/settings/modules", "/settings/usage", "/settings/errors"],
    forced: true,
    defaultEnabled: true,
  },
];

const MODULE_BY_ID: Record<string, ModuleSpec> = Object.fromEntries(
  MODULES.map((m) => [m.id, m]),
);

type Prefs = Record<string, unknown> | undefined | null;

export function isModuleEnabled(prefs: Prefs, moduleId: string): boolean {
  const m = MODULE_BY_ID[moduleId];
  if (!m) return true; // 未知 id：放行（forward-compat）
  if (m.forced) return true;
  const stored = (prefs?.modules as Record<string, boolean> | undefined)?.[moduleId];
  if (stored === undefined) return m.defaultEnabled;
  return Boolean(stored);
}

/** 找路径所属的模块；找不到（如 /login）返回 null = 不受模块管辖。 */
export function pathToModule(path: string): ModuleSpec | null {
  if (path === "/") return MODULE_BY_ID.core ?? null;
  let best: ModuleSpec | null = null;
  let bestLen = 0;
  for (const m of MODULES) {
    for (const p of m.paths) {
      if (p === "/") continue;
      if (path === p || path.startsWith(p + "/")) {
        if (p.length > bestLen) {
          bestLen = p.length;
          best = m;
        }
      }
    }
  }
  return best;
}

export function isPathEnabled(prefs: Prefs, path: string): boolean {
  const m = pathToModule(path);
  if (!m) return true;
  return isModuleEnabled(prefs, m.id);
}
